import { useState, useMemo } from 'react';
import type { EsmsOtpStats } from '../../types/esms';
import InfoTooltip from '../revenue/InfoTooltip';
import EsmsErrorBox from './EsmsErrorBox';

function fmt(v: number): string { return v.toLocaleString('en-US'); }

interface Props {
  data: EsmsOtpStats | null;
  isDark: boolean;
  isLoading: boolean;
  error: string | null;
  disabled: boolean;
}

export default function EsmsOtpStatsSection({ data, isDark, isLoading, error, disabled }: Props) {
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(50);

  const filtered = useMemo(() => {
    if (!data?.rows) return [];
    const q = search.trim();
    if (!q) return data.rows;
    return data.rows.filter(r => r.phone.includes(q));
  }, [data, search]);

  const prefix = data?.prefix || '(CTY_25FIT) Ma xac thuc cua ban la';

  return (
    <div className={`backdrop-blur rounded-lg p-4 sm:p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Thống kê SMS OTP
        <InfoTooltip isDark={isDark} text={`Thống kê các SMS có nội dung bắt đầu bằng:\n"${prefix}"\n\nGộp theo số điện thoại — cho biết mỗi khách hàng đã request OTP bao nhiêu lần, tổng chi phí, số thành công/thất bại.\n\nChỉ hỗ trợ khoảng thời gian ≤ 31 ngày.`} />
      </h3>

      {disabled ? (
        <div className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
          Tính năng này chỉ khả dụng khi khoảng thời gian ≤ 31 ngày. Hãy thu hẹp phạm vi để xem.
        </div>
      ) : isLoading ? (
        <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Đang tải...</div>
      ) : error ? (
        <EsmsErrorBox error={error} isDark={isDark} compact />
      ) : !data || data.rows.length === 0 ? (
        <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Không có SMS OTP nào trong kỳ</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <Stat label="Khách hàng" value={fmt(data.totals.phones)} isDark={isDark} accent="cyan" />
            <Stat label="KH dùng ≥2 SMS thành công" value={fmt(data.totals.phones_retry)} isDark={isDark} accent="amber" />
            <Stat label="Tổng SMS" value={fmt(data.totals.sms_count)} isDark={isDark} accent="cyan" />
            <Stat label="Tổng chi phí" value={fmt(data.totals.total_cost)} isDark={isDark} accent="amber" suffix="VND" />
            <Stat label="Thành công" value={fmt(data.totals.success_count)} isDark={isDark} accent="emerald" />
            <Stat label="Thất bại" value={fmt(data.totals.failed_count)} isDark={isDark} accent="rose" />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Lọc theo số điện thoại..."
              className={`flex-1 px-3 py-2 rounded border text-sm font-mono ${isDark ? 'bg-slate-800 border-white/10 text-slate-100 placeholder-slate-500' : 'bg-white border-gray-300 text-gray-900'}`}
            />
            <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              Hiển thị {Math.min(filtered.length, limit).toLocaleString('en-US')} / {filtered.length.toLocaleString('en-US')}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? 'border-b border-white/10' : 'border-b border-gray-200'}>
                  <th className={`text-left px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Số điện thoại</th>
                  <th className={`text-right px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Số SMS</th>
                  <th className={`text-right px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Tổng chi phí (VND)</th>
                  <th className={`text-right px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Thành công</th>
                  <th className={`text-right px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Thất bại</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, limit).map((r) => (
                  <tr key={r.phone} className={isDark ? 'border-b border-white/5' : 'border-b border-gray-100'}>
                    <td className={`px-2 sm:px-3 py-2 font-mono ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>{r.phone}</td>
                    <td className={`px-2 sm:px-3 py-2 text-right font-mono ${isDark ? 'text-cyan-300' : 'text-gray-900'}`}>{fmt(r.sms_count)}</td>
                    <td className={`px-2 sm:px-3 py-2 text-right font-mono ${isDark ? 'text-amber-300' : 'text-gray-900'}`}>{fmt(r.total_cost)}</td>
                    <td className={`px-2 sm:px-3 py-2 text-right font-mono ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>{fmt(r.success_count)}</td>
                    <td className={`px-2 sm:px-3 py-2 text-right font-mono ${r.failed_count > 0 ? (isDark ? 'text-rose-400' : 'text-rose-600') : (isDark ? 'text-slate-400' : 'text-gray-500')}`}>{fmt(r.failed_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length > limit && (
            <div className="flex justify-center mt-3">
              <button
                onClick={() => setLimit(limit + 50)}
                className={`px-3 py-1 rounded text-xs ${isDark ? 'bg-white/10 hover:bg-white/20 text-slate-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
              >
                Xem thêm 50
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, isDark, accent, suffix }: { label: string; value: string; isDark: boolean; accent: 'cyan' | 'amber' | 'emerald' | 'rose'; suffix?: string }) {
  const colorMap: Record<string, string> = {
    cyan: isDark ? 'text-cyan-300' : 'text-cyan-700',
    amber: isDark ? 'text-amber-300' : 'text-amber-700',
    emerald: isDark ? 'text-emerald-400' : 'text-emerald-700',
    rose: isDark ? 'text-rose-400' : 'text-rose-600',
  };
  return (
    <div className={`rounded p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
      <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{label}</div>
      <div className={`text-lg font-semibold font-mono ${colorMap[accent]}`}>
        {value}{suffix ? <span className="text-xs font-normal ml-1">{suffix}</span> : null}
      </div>
    </div>
  );
}
